<template>
  <b-container class="px-0 pt-0 topic-list-container" fluid>
    <!-- カテゴリ選択ダイアログ -->
    <category-modal-dialog
      id="modal-center"
      :categories="categories"
      :filter_init="categoryFilter"
      @onCloseCategoryFilterDialog="onCloseCategoryFilterDialog"
    />

    <b-row class="topic-list-header p-2 mx-0">
      <!-- カテゴリ選択ボタン(スマホ/サイドメニューのみ) -->
      <b-button
        class="category-selection-button d-md-none d-lg-none d-xl-none topic-list-category-selector"
        variant="outline-info my-auto"
        pill
        v-b-modal.modal-center
      >
        <div style="category-filter-label-container">
          <icon-checker class="mr-1" />
          <span class="category-filter-label" style="flex: 1 1 0; text-overflow: ellipsis">{{
            categoryFilterButtonLabel
          }}</span>
          <span>{{ categoryFilterButtonSuffix }}</span>
        </div>
      </b-button>

      <!-- 話題一覧ラベル(PC版のみ) -->
      <b-container class="d-none d-md-flex d-lg-flex d-xl-flex topic-list-label">
        <b-row align-v="center" class="font-weight-bold">話題一覧</b-row>
      </b-container>
    </b-row>

    <div class="w-100" style="height: 1px; background-color: #ddd"></div>

    <div v-if="!topicList.length && !isFetching" class="no-topics">{{ noItemsError }}</div>

    <!-- ピン留め -->
    <topic-list-item
      v-for="topic in topicPinnedSource"
      :key="topic.id"
      :id="topic.id"
      :icon="topic.icon"
      :category="mergedCategory(topic.category)"
      :title="topic.title"
      :desc="topic.desc"
      :updated-at="topic.updatedAt"
      :pinned="topic.pinned"
      :star="topic.star"
      :messages="topic.messages"
      :domain-id="domainId"
      :users="users"
      :viewer="me"
      :acl="topic.acl"
      :notification="topic.notification"
      :active="activeTopicId == topic.id"
      :in-sidemenu="inSidemenu"
    />

    <!-- ディバイダ(スマホのみ) -->
    <div
      v-if="topicList.length"
      class="w-100 d-md-none d-lg-none d-xl-none"
      style="height: 12px; background-color: #ddd"
    ></div>

    <!-- Star有り -->
    <topic-list-item
      v-for="topic in topicStarSource"
      :key="topic.id"
      :id="topic.id"
      :icon="topic.icon"
      :category="mergedCategory(topic.category)"
      :title="topic.title"
      :desc="topic.desc"
      :updated-at="topic.updatedAt"
      :pinned="topic.pinned"
      :star="topic.star"
      :messages="topic.messages"
      :domain-id="domainId"
      :users="users"
      :viewer="me"
      :acl="topic.acl"
      :active="activeTopicId == topic.id"
      :in-sidemenu="inSidemenu"
    />

    <!-- Star無し -->
    <topic-list-item
      v-for="topic in topicListSource"
      :key="topic.id"
      :id="topic.id"
      :icon="topic.icon"
      :category="mergedCategory(topic.category)"
      :title="topic.title"
      :desc="topic.desc"
      :updated-at="topic.updatedAt"
      :pinned="topic.pinned"
      :star="topic.star"
      :messages="topic.messages"
      :domain-id="domainId"
      :users="users"
      :viewer="me"
      :acl="topic.acl"
      :active="activeTopicId == topic.id"
      :in-sidemenu="inSidemenu"
    />

    <fab v-if="fab" v-on:fab-clicked="fabClicked" />

    <topic-edit
      v-if="fab"
      id="modal-topic"
      type="create"
      :categories="categories"
      :domainId="domainId"
      :allow_attachment_type="allow_attachment_type"
      :me="me"
      :isMobile="false"
    />
  </b-container>
</template>
<style type="scss" scoped></style>
<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  ref,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useAppStore, useCategoryStore, useDomainStore, useTopicStore } from "@/store";
import TopicListItem from "./TopicListItem.vue";
import IconChecker from "./icon/IconChecker.vue";
import CategoryModalDialog from "./CategoryModalDialog.vue";
import "vue-router";
import Fab from "./Fab.vue";
import "./topic-list.scss";
import { Category, Topic, User } from "../model";
import { IconReactionType } from "../API";
import { NavigationGuardNext, Route } from "vue-router";
import { ReactionController } from "@/model/reaction-controller";
import TopicEdit from "./TopicEdit.vue";
import { allowCreateTopic } from "../direct-app-config";
import type { AttachmentFileTypes } from "@/suppport-attachment-types";
import { AttachmentFileTypesDefault } from "@/suppport-attachment-types";

enum TypeFilter {
  None = "None",
  Star = "Star",
  Pin = "Pin",
  StarAndPin = "StarAndPin",
}

const topicFilterFunctor = (filters: Category[], topic: Topic) => {
  if (0 === filters.length) {
    return true;
  } else {
    return 0 <= filters.findIndex((filter) => filter.id == topic.category.id);
  }
};

export default defineComponent({
  name: "topic-list",
  components: {
    TopicListItem,
    IconChecker,
    CategoryModalDialog,
    Fab,
    TopicEdit,
  },
  props: {
    // 組織ID
    domainId: { type: String, default: () => "" },
    // カテゴリー一覧
    categories: { type: Array as PropType<Category[]>, default: () => [] },
    // 話題一覧
    topicList: { type: Array as PropType<Topic[]>, default: () => [] },
    // 現在アクティブな話題ID
    activeTopicId: { type: String, default: () => "" },
    // ユーザー一覧
    users: { type: Array as PropType<User[]>, default: () => [] },
    // ログインユーザー情報
    me: { type: Object as PropType<User>, default: () => User.createNotFoundUser() },
    // カテゴリーフィルタ
    categoryFilter: { type: Array as PropType<Category[]>, default: () => [] },
    // Fabボタンを表示するか
    fab: { type: Boolean, default: () => false },
    // true: サイドメニューの中
    inSidemenu: { type: Boolean, default: () => false },
  },
  // ナビゲーション前にデータをロードする
  beforeRouteEnter(to: Route, from: Route, next: NavigationGuardNext): void {
    // パラメータのリセット
    useAppStore().setTopicId("");
    useAppStore().setMessageId("");

    const to_domainId =
      to.params.domainId || (typeof to.query.domain_id == "string" ? to.query.domain_id : "");
    if (!to.query.search) {
      useAppStore().fetchData({ domainId: to_domainId, prevDomainId: from.params.domainId });
    }

    next();
  },
  beforeRouteUpdate(to: Route, from: Route, next: NavigationGuardNext): void {
    if (from.params.domainId != to.params.domainId) {
      useAppStore().fetchData({ domainId: to.params.domainId, prevDomainId: from.params.domainId });
    }
    next();
  },
  setup(props) {
    // $bvModal / $router は BootstrapVue / vue-router が生やす（Step 3 で composable 化予定）
    const vm = getCurrentInstance()?.proxy as Vue | undefined;

    const isMobile = ref<boolean>(false);

    const typeFilter = ref<TypeFilter>(TypeFilter.None); // Watchでフィルタするかどうか
    const allow_attachment_type = ref<AttachmentFileTypes>(AttachmentFileTypesDefault);

    let unsubscribeAction: (() => void) | undefined; //!< VuexのsubscribeAction結果

    const isFetching = computed((): boolean => useAppStore().fetching);

    const noItemsError = computed((): string => {
      // 取得できる話題が無い場合(閲覧権がない場合も同様)
      return "まだ話題はありません";
    });

    const newTopicUrl = computed((): string => {
      return `/${props.domainId}/new-topic`;
    });

    /* リアクション情報をマージした配列を返す */
    const managedTopicList = computed((): Topic[] => {
      const ctl = new ReactionController();
      props.topicList.forEach((topic) => {
        topic.star = ctl.getReaction(topic, IconReactionType.FAVORITE);
      });
      return props.topicList;
    });

    // カテゴリーフィルタのボタンラベル
    const categoryFilterButtonLabel = computed((): string => {
      switch (props.categoryFilter.length) {
        case 0:
          return "すべてのカテゴリー";
        default:
          return props.categoryFilter[0].title;
      }
    });
    const categoryFilterButtonSuffix = computed((): string => {
      return 1 < props.categoryFilter.length ? `他${props.categoryFilter.length - 1}件` : "";
    });

    const hasStar = (topic: Topic): boolean => {
      if (topic.star) {
        return topic.star.userList.some((u) => u.userId == props.me.directId);
      } else {
        return false;
      }
    };

    // 話題一覧（固定）
    const topicPinnedSource = computed((): Topic[] => {
      const result = managedTopicList.value.filter((topic) => {
        if (!topic.pinned) return false;
        return topicFilterFunctor(props.categoryFilter, topic);
      });
      result.sort((l, r) => {
        if (hasStar(l) && hasStar(r)) {
          // 固定 & ☆同士
          return r.updatedAt.getTime() - l.updatedAt.getTime();
        } else if (hasStar(l)) {
          // ☆の有る方が優先
          return -1;
        } else if (hasStar(r)) {
          // ☆の有る方が優先
          return 1;
        } else {
          return r.updatedAt.getTime() - l.updatedAt.getTime();
        }
      });
      return result;
    });

    // 話題一覧（★）
    const topicStarSource = computed((): Topic[] => {
      const result = managedTopicList.value.filter((topic) => {
        if (topic.pinned) return false;
        if (!hasStar(topic)) return false;
        return topicFilterFunctor(props.categoryFilter, topic);
      });
      result.sort((l, r) => r.updatedAt.getTime() - l.updatedAt.getTime());
      return result;
    });

    // 話題一覧（固定無し／★無し）
    const topicListSource = computed((): Topic[] => {
      if (typeFilter.value == TypeFilter.Star) return [];
      const result = managedTopicList.value.filter((topic) => {
        if (hasStar(topic) || topic.pinned) return false;
        return topicFilterFunctor(props.categoryFilter, topic);
      });
      result.sort((l, r) => r.updatedAt.getTime() - l.updatedAt.getTime());
      return result;
    });

    // topic.categoryは更新がされないため、更新が入るcategoriesを利用する
    const mergedCategory = (topicCategory: Category): Category => {
      const category = props.categories.find((c) => c.id == topicCategory.id);
      if (category) {
        return category;
      } else {
        return topicCategory;
      }
    };

    // Fabボタンをクリックされたときの処理
    const fabClicked = (): void => {
      if (allowCreateTopic(props.domainId) == false) {
        vm?.$root.$emit("free-alert");
        return;
      }

      // モバイル版の場合は新規作成画面を開く
      // その他の場合はモーダル
      if (isMobile.value) {
        vm?.$router.push(newTopicUrl.value);
      } else {
        vm?.$bvModal.show("modal-topic");
      }
    };

    /**
     * カテゴリーフィルタダイアログのクローズ時処理
     */
    const onCloseCategoryFilterDialog = (filter: Category[]): void => {
      useCategoryStore().setFilter({ domainId: props.domainId, filter });
    };

    const judgeMobile = (): void => {
      const mql = window.matchMedia("screen and (min-width: 768px)");
      isMobile.value = !mql.matches ? true : false;
    };

    // created 相当
    judgeMobile();
    window.addEventListener("resize", judgeMobile);

    // Lifecycle
    onMounted(() => {
      if (!unsubscribeAction) {
        // 旧 Vuex の topics/invalidate action の before サブスクリプションを $onAction で移植。
        unsubscribeAction = useTopicStore().$onAction(({ name, args }) => {
          if (name !== "invalidate") return; // invalidate以外無視
          const payload = args[0] as { domainId: string; topicId: string };
          if (payload.domainId != props.domainId) return; // 同組織以外無視
          // 指定話題の再ロードを行う
          useTopicStore().fetch(payload);
        });
      }
    });
    onUpdated(() => {
      allow_attachment_type.value = useDomainStore().getAllowAttachmentType as AttachmentFileTypes;
    });
    onBeforeUnmount(() => {
      if (unsubscribeAction) {
        unsubscribeAction();
        unsubscribeAction = undefined;
      }
    });

    return {
      isMobile,
      isFetching,
      typeFilter,
      allow_attachment_type,
      noItemsError,
      newTopicUrl,
      managedTopicList,
      categoryFilterButtonLabel,
      categoryFilterButtonSuffix,
      topicPinnedSource,
      topicStarSource,
      topicListSource,
      mergedCategory,
      fabClicked,
      onCloseCategoryFilterDialog,
      judgeMobile,
    };
  },
});
</script>
